// Admin portal (plan 0004): ADMIN_TOKEN login (not a user account),
// usage overview, user disable toggle, recent meetings.

import { html, LitElement } from "lit";
import {
  admin,
  ApiError,
  type AdminMeetingRow,
  type AdminUserRow,
} from "../lib/api";

const TOKEN_KEY = "mm-admin-token";

export class MmAdmin extends LitElement {
  private token: string | null = sessionStorage.getItem(TOKEN_KEY);
  private overview: Record<string, unknown> | null = null;
  private users: AdminUserRow[] = [];
  private meetings: AdminMeetingRow[] = [];
  private error = "";
  private busy = false;

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    if (this.token) void this.load();
  }

  private async load(): Promise<void> {
    if (!this.token) return;
    this.busy = true;
    this.requestUpdate();
    try {
      const [o, u, m] = await Promise.all([
        admin.overview(this.token),
        admin.users(this.token),
        admin.meetings(this.token),
      ]);
      this.overview = o.overview;
      this.users = u.users;
      this.meetings = m.meetings;
      this.error = "";
      sessionStorage.setItem(TOKEN_KEY, this.token);
    } catch (e) {
      this.overview = null;
      this.error =
        e instanceof ApiError && e.status === 401
          ? "invalid admin token"
          : e instanceof ApiError && e.status === 404
            ? "admin portal is not enabled on this server (set ADMIN_TOKEN)"
            : "failed to load admin data";
      sessionStorage.removeItem(TOKEN_KEY);
      if (this.error === "invalid admin token") this.token = null;
    }
    this.busy = false;
    this.requestUpdate();
  }

  private submitToken(e: Event): void {
    e.preventDefault();
    const input = this.querySelector("input[name=token]") as HTMLInputElement;
    this.token = input.value.trim();
    void this.load();
  }

  private async toggleDisabled(u: AdminUserRow): Promise<void> {
    if (!this.token) return;
    const verb = u.disabled ? "Enable" : "Disable";
    if (!confirm(`${verb} ${u.email}? ${u.disabled ? "" : "Their sessions end immediately."}`))
      return;
    try {
      await admin.setDisabled(this.token, u.id, !u.disabled);
      await this.load();
    } catch (e) {
      this.error = e instanceof ApiError ? e.message : "action failed";
      this.requestUpdate();
    }
  }

  private metric(label: string, value: unknown, sub = "") {
    return html`<div class="metric-card">
      <p class="metric-label">${label}</p>
      <p class="metric-value">${String(value ?? "—")}</p>
      ${sub ? html`<p class="metric-sub">${sub}</p>` : ""}
    </div>`;
  }

  override render() {
    if (!this.token || !this.overview) {
      return html`
        <section class="summary">
          <p class="eyebrow">Operator</p>
          <h1>Admin portal</h1>
        </section>
        ${this.error
          ? html`<div class="message-card message-card-error"><p>${this.error}</p></div>`
          : ""}
        <section>
          <form class="login-form" @submit=${this.submitToken}>
            <div>
              <label class="field-label" for="token">Admin token</label>
              <input id="token" name="token" type="password" required
                placeholder="ADMIN_TOKEN from the server environment" />
            </div>
            <button class="primary-button" ?disabled=${this.busy}>
              ${this.busy ? "Checking…" : "Enter"}
            </button>
          </form>
        </section>
      `;
    }
    const o = this.overview;
    const gb = (b: unknown) => `${(Number(b ?? 0) / 1_048_576).toFixed(1)} MB`;
    const hours = (s: unknown) => `${(Number(s ?? 0) / 3600).toFixed(1)} h`;
    return html`
      <section class="summary">
        <p class="eyebrow">Operator</p>
        <div class="timeline-head">
          <h1>Admin portal</h1>
          <span class="row">
            <span class="badge badge-accent">v${o.version}</span>
            <span class="badge">${o.provider}</span>
            <span class="badge ${o.registration_open ? "badge-warn" : "badge-ok"}">
              registration ${o.registration_open ? "open" : "closed"}
            </span>
          </span>
        </div>
        <div class="toolbar">
          <button class="ghost-button" @click=${() => void this.load()}>↻ Refresh</button>
          <span class="grow"></span>
          <a class="navlink" href="/metrics" target="_blank">/metrics →</a>
        </div>
      </section>
      ${this.error
        ? html`<div class="message-card message-card-error"><p>${this.error}</p></div>`
        : ""}
      <div class="metric-grid" style="margin-bottom: 1rem">
        ${this.metric("Users", o.users_total, `${o.users_disabled} disabled`)}
        ${this.metric(
          "Meetings",
          o.meetings_total,
          `${o.meetings_active} active · ${o.meetings_hosted} hosted`,
        )}
        ${this.metric("Transcribed", hours(o.billed_stt_seconds_total),
          `${o.segments_total} segments`)}
        ${this.metric("Summaries", o.summaries_complete, "completed")}
        ${this.metric("Guests", o.guests_total, "across all meetings")}
        ${this.metric("Database", gb(o.db_size_bytes), "SQLite size")}
      </div>
      <section>
        <h2>Users</h2>
        <div class="timeline">
          ${this.users.map(
            (u) => html`
              <div class="timeline-item">
                <div class="timeline-head">
                  <span class="row">
                    <strong>${u.email}</strong>
                    ${u.disabled
                      ? html`<span class="badge badge-danger">disabled</span>`
                      : ""}
                  </span>
                  <button
                    class=${u.disabled ? "ghost-button" : "danger-button"}
                    @click=${() => this.toggleDisabled(u)}
                  >
                    ${u.disabled ? "Enable" : "Disable"}
                  </button>
                </div>
                <p class="muted">
                  ${u.meeting_count} meetings · ${(u.billed_stt_seconds / 60).toFixed(0)}m
                  transcribed · joined ${new Date(u.created_at).toLocaleDateString()} ·
                  last session
                  ${u.last_session_at
                    ? new Date(u.last_session_at).toLocaleString()
                    : "never"}
                </p>
              </div>
            `,
          )}
        </div>
      </section>
      <section>
        <h2>Recent meetings</h2>
        <div class="timeline">
          ${this.meetings.map(
            (m) => html`
              <div class="timeline-item">
                <div class="timeline-head">
                  <strong>${m.title}</strong>
                  <span class="row">
                    <span class="badge">${m.kind}</span>
                    <span class="badge badge-accent">${m.state}</span>
                  </span>
                </div>
                <p class="muted">
                  ${m.owner_email} · ${m.participant_count} participants ·
                  ${m.segment_count} segments · ${m.billed_stt_seconds}s STT ·
                  ${new Date(m.created_at).toLocaleString()}
                </p>
              </div>
            `,
          )}
        </div>
      </section>
    `;
  }
}

customElements.define("mm-admin", MmAdmin);
